/**
 * Stage 9: AI Query Generation — context-based version using StagePanel.
 *
 * Generates search queries the article should rank for in AI search engines.
 * LLM stage. Query editing goes through the document editor.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function renderOutput(doc: ArticleDocument) {
  const rawQueries = doc.preflight?.ai_queries;
  // ai_queries may be an array directly or {queries: [...]}
  const queries = Array.isArray(rawQueries) ? rawQueries
    : Array.isArray((rawQueries as any)?.queries) ? (rawQueries as any).queries
    : null;
  if (!queries || queries.length === 0) {
    return <div className="text-sm text-gray-500">No AI queries generated yet.</div>;
  }
  const grouped = {
    DIRECT: queries.filter((q: any) => q.category === 'DIRECT'),
    PARTIAL: queries.filter((q: any) => q.category === 'PARTIAL'),
    'LONG-TAIL': queries.filter((q: any) => q.category === 'LONG-TAIL'),
  };
  return (
    <div className="space-y-2">
      {Object.entries(grouped).map(([category, items]) => (
        items.length > 0 && (
          <div key={category}>
            <div className="text-xs font-medium text-gray-700 mb-1">{category}</div>
            {items.map((q: any, i: number) => (
              <div key={i} className="text-xs text-gray-600 pl-2 border-l-2 border-gray-200 mb-1">
                {q.query}
              </div>
            ))}
          </div>
        )
      ))}
    </div>
  );
}

export function Stage9AiQueriesV2() {
  const { document } = useArticleContext();
  const hasMarkdown = Boolean(document?.article?.markdown);

  return (
    <StagePanel
      stageId="stage_9_ai_queries"
      isLlmStage
      title="Stage 9: AI Query Generation"
      description="Generates natural language search queries for AI search visibility."
      runDisabled={!hasMarkdown}
      runDisabledReason={!hasMarkdown ? 'Requires finalized article markdown' : undefined}
      renderOutput={renderOutput}
    >
      {!hasMarkdown && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded border p-2">
          Waiting for article markdown from earlier stages.
        </div>
      )}
    </StagePanel>
  );
}
